import React from 'react';
import HeroContact from '../components/HeroContact';
import ContactForm from '../components/ContactForm';
import { ReactComponent as TikTokIcon } from '../assets/icons/tiktok.svg';
import { useLanguage, t } from '../utils/i18n';

const ContactPage = () => {
  const { language } = useLanguage();

  return (
    <div className="relative min-h-screen">
      <div className="absolute inset-0 bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3] z-0" />

      <div className="relative z-10">
        <HeroContact 
          title={t('contact.hero.title', language.code)}
          subtitle={t('contact.hero.subtitle', language.code)}
        />
        
        <div className="py-16">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="p-8 rounded-[2rem] bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
                <h2 className="text-3xl font-cursive text-akasha-brown mb-6">{t('contact.info.title', language.code)}</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="text-akasha-brown mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-akasha-black">{t('contact.info.address.title', language.code)}</h3>
                      <p className="text-gray-700">
                        {t('contact.info.address.line1', language.code)}<br />
                        {t('contact.info.address.line2', language.code)}<br />
                        {t('contact.info.address.line3', language.code)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="text-akasha-brown mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-akasha-black">{t('contact.info.phone.title', language.code)}</h3>
                      <p className="text-gray-700">{t('contact.info.phone.number', language.code)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="text-akasha-brown mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-akasha-black">{t('contact.info.email.title', language.code)}</h3>
                      <p className="text-gray-700">
                        <a href={`mailto:${t('contact.info.email.address', language.code)}`} className="hover:text-akasha-brown">{t('contact.info.email.address', language.code)}</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="text-akasha-brown mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-akasha-black">{t('contact.info.hours.title', language.code)}</h3>
                      <p className="text-gray-700">
                        {t('contact.info.hours.time', language.code)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-akasha-black mb-4">{t('contact.info.social.title', language.code)}</h3>
                  <div className="flex space-x-4">
                    <a 
                      href="https://www.facebook.com/akashaconceptstore/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="text-akasha-black hover:text-akasha-brown transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                      </svg>
                    </a>
                    <a 
                      href="https://www.instagram.com/akashaconceptstore/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="text-akasha-black hover:text-akasha-brown transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>
                    <a 
                      href="https://www.tiktok.com/@akasha.concept.st" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="TikTok"
                      className="text-akasha-black hover:text-akasha-brown transition-colors"
                    >
                      <TikTokIcon className="h-6 w-6" fill="currentColor" />
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="p-8 rounded-[2rem] bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
                <h2 className="text-3xl font-cursive text-akasha-brown mb-6">{t('contact.form.title', language.code)}</h2>
                <ContactForm />
              </div>
            </div>
            
            <div className="mt-16">
              <h2 className="text-3xl font-cursive text-akasha-brown mb-8 text-center">{t('contact.map.heading', language.code)}</h2>
              <div className="rounded-lg overflow-hidden shadow-md border border-akasha-brown/30">
                <iframe 
                  title={t('contact.map.title', language.code)}
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3764.607617936661!2d-99.17412932549344!3d19.342993442654863!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85d1ffc28d341607%3A0xf5217e97d51983c9!2sAkasha%20Concept%20Store!5e0!3m2!1ses-419!2smx!4v1701118314763!5m2!1ses-419!2smx"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                >
                </iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
